
import { Json } from "../integrations/supabase/types";
import MarkdownIt from 'markdown-it';
import DOMPurify from 'dompurify';

const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true
});

export interface FormattedText {
  type: 'paragraph' | 'heading' | 'bullet' | 'blockquote' | 'metadata';
  text: string;
  level?: 1 | 2 | 3;
  metadata?: Record<string, any>;
}

// Initialize DOMPurify in a safe manner
const getSanitizer = () => {
  if (typeof window === 'undefined') {
    return {
      sanitize: (html: string) => html
    };
  }
  return DOMPurify;
};

export function formatKeyPoints(keyPointsData: string | Record<string, any> | null | undefined): string {
  try {
    if (keyPointsData === null || keyPointsData === undefined) {
      return "<h2>Key Points</h2><p>No key points available yet</p>";
    }
    
    console.log('formatKeyPoints input type:', typeof keyPointsData);
    
    // Handle string input safely
    let data: any;
    if (typeof keyPointsData === 'string') {
      try {
        // Check if it's already HTML content
        if (keyPointsData.trim().startsWith('<') && keyPointsData.includes('</')) {
          return sanitizeHtml(keyPointsData, true);
        }
        
        data = JSON.parse(keyPointsData);
      } catch (e) {
        console.error('Error parsing key points string:', e);
        return "<h2>Key Points</h2><p>Error parsing key points</p>";
      }
    } else {
      data = keyPointsData;
    }
    
    if (!data || typeof data !== 'object') {
      console.warn('Invalid key points data format:', data);
      return "<h2>Key Points</h2><p>No key points available</p>";
    }

    let formattedText = '';
    
    // Format topics
    if (Array.isArray(data.topics) && data.topics.length > 0) {
      data.topics.forEach((topic: any) => {
        if (topic?.topic && Array.isArray(topic?.key_points)) {
          formattedText += `<h2>${sanitizeHtml(topic.topic)}</h2><ul>`;
          topic.key_points.forEach((point: string) => {
            formattedText += `<li>${sanitizeHtml(point)}</li>`;
          });
          formattedText += '</ul>';
        }
      });
    } else {
      console.log('No topics found in key points data');
    }
    
    // Format final takeaways
    if (Array.isArray(data.final_takeaways) && data.final_takeaways.length > 0) {
      formattedText += '<h2>Final Takeaways</h2><ul>';
      data.final_takeaways.forEach((takeaway: string) => {
        formattedText += `<li>${sanitizeHtml(takeaway)}</li>`;
      });
      formattedText += '</ul>';
    } else {
      console.log('No final takeaways found in key points data');
    }
    
    // If we have no content at all, add placeholder
    if (!formattedText) {
      formattedText = "<h2>Key Points</h2><p>No key points available yet</p>";
    }
    
    return formattedText;
    
  } catch (error) {
    console.error('Error formatting key points:', error);
    return "<h2>Key Points</h2><p>Error formatting key points</p>";
  }
}

export function formatDetailedNotes(notesData: string | Record<string, any> | null | undefined): string {
  try {
    if (notesData === null || notesData === undefined) {
      return "<h2>Notes</h2><p>No detailed notes available yet</p>";
    }
    
    console.log('Formatting detailed notes, input type:', typeof notesData);
    
    // Handle string input safely
    let data: any;
    if (typeof notesData === 'string') {
      try {
        // First check if it's already HTML content
        if (notesData.trim().startsWith('<') && notesData.includes('</')) {
          return sanitizeHtml(notesData, true); // Return sanitized HTML
        }
        
        data = JSON.parse(notesData);
      } catch (e) {
        console.error('Error parsing detailed notes string:', e);
        // If parsing fails, it might be direct HTML content
        if (typeof notesData === 'string' && notesData.includes('<h2>')) {
          return sanitizeHtml(notesData, true); // Return sanitized HTML
        }
        return "<h2>Notes</h2><p>Error parsing detailed notes</p>";
      }
    } else {
      data = notesData;
    }
    
    // Get the detailed notes array, handling both formats
    const detailedNotes = Array.isArray(data) ? data : data?.detailed_notes || [];
    
    if (!Array.isArray(detailedNotes) || detailedNotes.length === 0) {
      console.log('No detailed notes found or invalid format');
      return "<h2>Notes</h2><p>No detailed notes available yet</p>";
    }
    
    const formattedText = detailedNotes.map(section => {
      if (!section?.topic) {
        return '<h2>Untitled Section</h2><p>No content</p>';
      }
      
      const safeTopic = sanitizeHtml(section.topic);
      const safeDetails = section.details 
        ? sanitizeHtml(section.details, true) 
        : '<p>No details available</p>';
      
      return `<h2>${safeTopic}</h2>${safeDetails}`;
    }).join('');
    
    console.log('Formatted detailed notes output length:', formattedText.length);
    return formattedText || "<h2>Notes</h2><p>No detailed notes available</p>";
    
  } catch (error) {
    console.error('Error formatting detailed notes:', error);
    return "<h2>Notes</h2><p>Error formatting detailed notes</p>";
  }
}

export function parseJsonSafely(value: string | null | undefined | Record<string, any>): any {
  if (value === null || value === undefined) return null;
  if (typeof value === 'object') return value;
  
  try {
    // If it looks like HTML, return it as is
    if (typeof value === 'string' && value.trim().startsWith('<') && value.includes('</')) {
      return value;
    }
    
    return JSON.parse(value);
  } catch (error) {
    console.error('Error parsing JSON:', error);
    // If it failed to parse but has content, return the original string
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
    return null;
  }
}

// Enhanced HTML sanitization function
function sanitizeHtml(text: string | null | undefined, allowHtml = false): string {
  if (!text) return '';
  
  // Use DOMPurify for safer HTML sanitization
  const sanitizer = getSanitizer();
  
  // If HTML is allowed, sanitize without stripping tags
  if (allowHtml && text.includes('<') && text.includes('>')) {
    return sanitizer.sanitize(text, {
      ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'br', 'ul', 'ol', 'li', 'strong', 'em', 'u', 'blockquote', 'a'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class'],
    });
  }
  
  // Basic text sanitization (convert to plain text)
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}
